import React from 'react';
import Label from '@/components/ui/Label';
import PrimaryInput from '@/components/ui/PrimaryInput';
import {
  IFormColumn,
  IFormSubmissionValue,
  InputType,
} from '@/interfaces/form.model';

interface IDynamicFormInputProps {
  column: IFormColumn;
  value: IFormSubmissionValue;
  error?: string;
  onChange: (value: IFormSubmissionValue) => void;
  identifier: string;
  isDisable?: boolean;
}

const DynamicFormInput: React.FC<IDynamicFormInputProps> = ({
  column,
  value,
  error,
  onChange,
  identifier,
  isDisable = false,
}) => {
  const type = (column.columnDatatype || 'text').toLowerCase();
  switch (type) {
    case 'textarea':
      return (
        <div className="mb-5">
          <Label
            id={`label-${identifier}`}
            required={column.required}
            labelClassname="d-block form-label fw-semibold"
          >
            {column.label}
          </Label>
          <textarea
            className={`form-control ${error ? 'is-invalid' : ''}`}
            rows={4}
            placeholder={column.placeholder || `Enter ${column.label}`}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => onChange(event.target.value)}
            disabled={isDisable}
          />
          {error ? (
            <div className="invalid-feedback d-block">{error}</div>
          ) : (
            column.helpText && (
              <small className="text-muted mt-1 d-block">
                {column.helpText}
              </small>
            )
          )}
        </div>
      );

    case 'select':
      return (
        <div className="mb-5">
          <Label
            id={`label-${identifier}`}
            required={column.required}
            labelClassname="form-label fw-semibold d-block"
          >
            {column.label}
          </Label>
          <select
            className={`form-select form-control ${error ? 'is-invalid' : ''}`}
            value={typeof value === 'string' ? value : ''}
            onChange={(event) => onChange(event.target.value)}
            disabled={isDisable}
          >
            <option value="">Select {column.label}</option>
            {column.options?.map((option) => (
              <option
                key={`${identifier}-${option.value}`}
                value={option.value}
              >
                {option.label}
              </option>
            ))}
          </select>
          {error ? (
            <div className="invalid-feedback d-block">{error}</div>
          ) : (
            column.helpText && (
              <small className="text-muted mt-1 d-block">
                {column.helpText}
              </small>
            )
          )}
        </div>
      );

    case 'radio':
      return (
        <div className="mb-5">
          <Label
            id={`label-${identifier}`}
            required={column.required}
            labelClassname="d-block form-label fw-semibold"
          >
            {column.label}
          </Label>
          <div
            className={`row row-gap-3 mt-2 ms-1 ${error ? 'is-invalid p-2 border rounded' : ''}`}
          >
            {column.options?.map((option) => {
              const checked = value === option.value;

              return (
                <div
                  key={`${identifier}-${option.value}`}
                  className="form-check col-md-12"
                >
                  <input
                    className="form-check-input"
                    type="radio"
                    name={identifier}
                    disabled={isDisable}
                    id={`${identifier}-${option.value}`}
                    checked={checked}
                    onChange={() => onChange(option.value)}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`${identifier}-${option.value}`}
                  >
                    {option.label}
                  </label>
                </div>
              );
            })}
          </div>
          {error ? (
            <div className="invalid-feedback d-block">{error}</div>
          ) : (
            column.helpText && (
              <small className="text-muted mt-1 d-block">
                {column.helpText}
              </small>
            )
          )}
        </div>
      );

    case 'checkbox':
      return (
        <div className="mb-5">
          <Label
            id={`label-${identifier}`}
            required={column.required}
            labelClassname="d-block form-label fw-semibold"
          >
            {column.label}
          </Label>
          <div
            className={`row row-gap-3 mt-2 ms-1 ${error ? 'is-invalid p-2 border rounded' : ''}`}
          >
            {column.options?.map((option) => {
              const selectedValues = Array.isArray(value)
                ? (value as string[])
                : [];
              const checked = selectedValues.includes(option.value);

              return (
                <div
                  key={`${identifier}-${option.value}`}
                  className="form-check col-md-4"
                >
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name={identifier}
                    id={`${identifier}-${option.value}`}
                    checked={checked}
                    disabled={isDisable}
                    onChange={(event) => {
                      const nextValues = Array.isArray(value)
                        ? [...(value as string[])]
                        : [];
                      if (event.target.checked) {
                        nextValues.push(option.value);
                      } else {
                        const removeIndex = nextValues.indexOf(option.value);
                        if (removeIndex > -1) nextValues.splice(removeIndex, 1);
                      }
                      onChange(nextValues);
                    }}
                  />
                  <label
                    className="form-check-label"
                    htmlFor={`${identifier}-${option.value}`}
                  >
                    {option.label}
                  </label>
                </div>
              );
            })}
          </div>
          {error ? (
            <div className="invalid-feedback d-block">{error}</div>
          ) : (
            column.helpText && (
              <small className="text-muted mt-1 d-block">
                {column.helpText}
              </small>
            )
          )}
        </div>
      );

    case 'file':
      return (
        <div className="mb-5">
          <Label
            id={`label-${identifier}`}
            required={column.required}
            labelClassname="form-label fw-semibold d-block"
          >
            {column.label}
          </Label>
          <input
            type="file"
            disabled={isDisable}
            className={`form-control ${error ? 'is-invalid' : ''}`}
            onChange={(event) => onChange(event.target.files?.[0] ?? null)}
          />
          {error ? (
            <div className="invalid-feedback d-block">{error}</div>
          ) : (
            column.helpText && (
              <small className="text-muted mt-1 d-block">
                {column.helpText}
              </small>
            )
          )}
        </div>
      );

    default: {
      const inputType = [
        'text',
        'email',
        'date',
        'number',
        'password',
        'url',
        'tel',
      ].includes(type)
        ? (type as InputType)
        : 'text';

      return (
        <div className="mb-5">
          <PrimaryInput
            label={column.label}
            type={inputType}
            disabled={isDisable}
            placeholder={column.placeholder || `Enter ${column.label}`}
            required={column.required}
            value={
              typeof value === 'string' || typeof value === 'number'
                ? String(value)
                : ''
            }
            onChange={(event) => onChange(event.target.value)}
            className={error ? 'is-invalid' : ''}
          />
          {error ? (
            <div
              className="invalid-feedback d-block"
              style={{ marginTop: '-0.75rem', marginBottom: '0.5rem' }}
            >
              {error}
            </div>
          ) : (
            column.helpText && (
              <small
                className="text-muted mt-1 d-block"
                style={{ marginTop: '-1rem' }}
              >
                {column.helpText}
              </small>
            )
          )}
        </div>
      );
    }
  }
};

export default DynamicFormInput;
